import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Home, Hotel, CreditCard, MapPin, Clock, Coffee } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getAdminConfig, updateAdminConfig, getAdminMe } from '@/lib/api';

const AdminConfig = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    domaine_rooms: 10,
    hotel_rooms: 20,
    price_per_room: 100,
    price_per_person: 50,
    price_mode: 'per_room',
    minimum_amount: 30,
    show_quota: true,
    brunch_enabled: true,
    mairie_time: '14:00',
    domaine_time: '16:00',
    mairie_address: 'Mairie de Sainte-Eulalie, 33560 Sainte-Eulalie',
    domaine_address: 'Domaine de réception, Bordeaux',
    paypal_email: 'perez-cedric@hotmail.fr',
    paylib_phone: '',
    rib: { iban: '', bic: '', bank: '', holder: '' },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getAdminMe();
      const response = await getAdminConfig();
      setConfig((prev) => ({ ...prev, ...response.data }));
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdminConfig(config);
      toast.success('Configuration sauvegardée !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const updateRib = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      rib: { ...prev.rib, [field]: value },
    }));
  };

  if (loading) {
    return (
      <AdminLayout title="Configuration">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Configuration">
      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">Général</TabsTrigger>
          <TabsTrigger value="accommodation" data-testid="tab-accommodation">Hébergement</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">Paiement</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-terracotta" />
                Horaires
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mairie_time">Heure cérémonie (Mairie)</Label>
                  <Input
                    id="mairie_time"
                    data-testid="input-mairie-time"
                    type="time"
                    value={config.mairie_time}
                    onChange={(e) => updateField('mairie_time', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="domaine_time">Heure arrivée (Domaine)</Label>
                  <Input
                    id="domaine_time"
                    data-testid="input-domaine-time"
                    type="time"
                    value={config.domaine_time}
                    onChange={(e) => updateField('domaine_time', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-terracotta" />
                Adresses
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="mairie_address">Adresse Mairie</Label>
                <Textarea
                  id="mairie_address"
                  data-testid="input-mairie-address"
                  value={config.mairie_address}
                  onChange={(e) => updateField('mairie_address', e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="domaine_address">Adresse Domaine</Label>
                <Textarea
                  id="domaine_address"
                  data-testid="input-domaine-address"
                  value={config.domaine_address}
                  onChange={(e) => updateField('domaine_address', e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coffee className="w-5 h-5 text-terracotta" />
                Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="brunch_enabled" className="text-base">Brunch le lendemain</Label>
                  <p className="text-sm text-gray-500">Afficher le brunch dans le programme</p>
                </div>
                <Switch
                  id="brunch_enabled"
                  data-testid="switch-brunch"
                  checked={config.brunch_enabled}
                  onCheckedChange={(checked) => updateField('brunch_enabled', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Accommodation Settings */}
        <TabsContent value="accommodation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-terracotta" />
                Capacités
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="domaine_rooms">Chambres Domaine</Label>
                  <Input
                    id="domaine_rooms"
                    data-testid="input-domaine-rooms"
                    type="number"
                    min="0"
                    value={config.domaine_rooms}
                    onChange={(e) => updateField('domaine_rooms', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="hotel_rooms">Chambres Hôtel</Label>
                  <Input
                    id="hotel_rooms"
                    data-testid="input-hotel-rooms"
                    type="number"
                    min="0"
                    value={config.hotel_rooms}
                    onChange={(e) => updateField('hotel_rooms', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between pt-4">
                <div>
                  <Label htmlFor="show_quota" className="text-base">Afficher les disponibilités</Label>
                  <p className="text-sm text-gray-500">Montrer le nombre de chambres restantes aux invités</p>
                </div>
                <Switch
                  id="show_quota"
                  data-testid="switch-quota"
                  checked={config.show_quota}
                  onCheckedChange={(checked) => updateField('show_quota', checked)}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-terracotta" />
                Tarification
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="price_mode">Mode de tarification</Label>
                <Select
                  value={config.price_mode}
                  onValueChange={(value) => updateField('price_mode', value)}
                >
                  <SelectTrigger data-testid="select-price-mode" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per_room">Par chambre</SelectItem>
                    <SelectItem value="per_person">Par personne</SelectItem>
                    <SelectItem value="fixed">Forfait fixe</SelectItem>
                    <SelectItem value="libre">Montant libre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price_per_room">Prix par chambre (€)</Label>
                  <Input
                    id="price_per_room"
                    data-testid="input-price-room"
                    type="number"
                    min="0"
                    value={config.price_per_room}
                    onChange={(e) => updateField('price_per_room', parseFloat(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="price_per_person">Prix par personne (€)</Label>
                  <Input
                    id="price_per_person"
                    data-testid="input-price-person"
                    type="number"
                    min="0"
                    value={config.price_per_person}
                    onChange={(e) => updateField('price_per_person', parseFloat(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
              </div>
              {config.price_mode === 'libre' && (
                <div>
                  <Label htmlFor="minimum_amount">Montant minimum (€)</Label>
                  <Input
                    id="minimum_amount"
                    data-testid="input-minimum"
                    type="number"
                    min="0"
                    value={config.minimum_amount}
                    onChange={(e) => updateField('minimum_amount', parseFloat(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings */}
        <TabsContent value="payment" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>PayPal</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="paypal_email">Email PayPal</Label>
                <Input
                  id="paypal_email"
                  data-testid="input-paypal-email"
                  type="email"
                  value={config.paypal_email}
                  onChange={(e) => updateField('paypal_email', e.target.value)}
                  className="mt-1"
                  placeholder="exemple@email.com"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Paylib</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="paylib_phone">Numéro de téléphone Paylib</Label>
                <Input
                  id="paylib_phone"
                  data-testid="input-paylib-phone"
                  type="tel"
                  value={config.paylib_phone}
                  onChange={(e) => updateField('paylib_phone', e.target.value)}
                  className="mt-1"
                  placeholder="06 12 34 56 78"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Laissez vide pour désactiver Paylib
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Virement bancaire (RIB)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="rib_holder">Titulaire du compte</Label>
                <Input
                  id="rib_holder"
                  data-testid="input-rib-holder"
                  value={config.rib?.holder || ''}
                  onChange={(e) => updateRib('holder', e.target.value)}
                  className="mt-1"
                  placeholder="Nom du titulaire"
                />
              </div>
              <div>
                <Label htmlFor="rib_iban">IBAN</Label>
                <Input
                  id="rib_iban"
                  data-testid="input-rib-iban"
                  value={config.rib?.iban || ''}
                  onChange={(e) => updateRib('iban', e.target.value)}
                  className="mt-1"
                  placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rib_bic">BIC</Label>
                  <Input
                    id="rib_bic"
                    data-testid="input-rib-bic"
                    value={config.rib?.bic || ''}
                    onChange={(e) => updateRib('bic', e.target.value)}
                    className="mt-1"
                    placeholder="XXXXXXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="rib_bank">Banque</Label>
                  <Input
                    id="rib_bank"
                    data-testid="input-rib-bank"
                    value={config.rib?.bank || ''}
                    onChange={(e) => updateRib('bank', e.target.value)}
                    className="mt-1"
                    placeholder="Nom de la banque"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end pt-6">
        <Button
          data-testid="btn-save-config"
          onClick={handleSave}
          disabled={saving}
          className="bg-terracotta hover:bg-terracotta/90"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminConfig;
